import { Controller } from "@hotwired/stimulus"

// Shows/hides content elements based on whether any trigger checkbox is checked.
// Triggers, content, and required inputs are associated into independent groups via
// a `data-reveal-group` attribute, so a single controller can manage several reveal
// relationships (e.g. waiver, driving) without nesting.
// - trigger targets: checkboxes that control visibility for their group(s)
// - content targets: elements to show/hide for their group(s)
// - required targets: inputs that become required when their group's content is shown
//
// An element may belong to several groups by listing them space-separated, so one
// trigger can reveal more than one section. A group reveals when ANY of its triggers
// is checked. When hiding, content's checkboxes are unchecked and a `change` event is
// dispatched, so dependent groups nested inside that content collapse too.
//
//   <form data-controller="reveal">
//     <!-- this event needs BOTH a waiver and driving info, so it triggers both groups -->
//     <input type="checkbox" data-reveal-target="trigger" data-reveal-group="waiver driving"
//            data-action="change->reveal#update">
//     <!-- this event only needs a waiver -->
//     <input type="checkbox" data-reveal-target="trigger" data-reveal-group="waiver"
//            data-action="change->reveal#update">
//
//     <div data-reveal-target="content" data-reveal-group="waiver" hidden>
//       <input data-reveal-target="required" data-reveal-group="waiver">
//     </div>
//
//     <div data-reveal-target="content" data-reveal-group="driving" hidden>
//       <!-- nested relationship, declared flat: this trigger drives the "has_car" group -->
//       <input type="checkbox" data-reveal-target="trigger" data-reveal-group="has_car"
//              data-action="change->reveal#update">
//     </div>
//     <div data-reveal-target="content" data-reveal-group="has_car" hidden>...</div>
//   </form>
//
// Checking either event reveals the "waiver" content; only the first also reveals
// "driving". Unchecking "driving" content unchecks the "has_car" trigger inside it and
// dispatches `change`, so the "has_car" group collapses as well.
export default class extends Controller {
  static targets = ["trigger", "content", "required"]

  connect() {
    this.groups.forEach(group => this.updateGroup(group))
  }

  update(event) {
    this.groupsFor(event.currentTarget).forEach(group => this.updateGroup(group))
  }

  updateGroup(group) {
    const anyChecked = this.triggerTargets
      .filter(t => this.groupsFor(t).includes(group))
      .some(t => t.checked)

    this.contentTargets
      .filter(el => this.groupsFor(el).includes(group))
      .forEach(el => {
        el.hidden = !anyChecked
        if (!anyChecked) {
          el.querySelectorAll("input[type=checkbox]").forEach(cb => {
            cb.checked = false
            cb.dispatchEvent(new Event("change", { bubbles: true }))
          })
        }
      })

    this.requiredTargets
      .filter(el => this.groupsFor(el).includes(group))
      .forEach(el => { el.required = anyChecked })
  }

  groupsFor(el) {
    return (el.dataset.revealGroup || "").split(/\s+/).filter(Boolean)
  }

  get groups() {
    return [...new Set(this.triggerTargets.flatMap(t => this.groupsFor(t)))]
  }
}
